import type { MouseEvent, ReactNode } from 'react';
import * as React from 'react';
import { memo, useMemo, useRef, useState } from 'react';
import { noop } from 'lodash';
import styled from 'styled-components';
import { usePrefetchForShareBuilder } from '@frameio/assets/shares/hooks';
import { useShareNavItemFragment } from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import {
  useActiveProjectId,
  useOpenContextMenu,
} from '@frameio/shared/contexts';
import { useGlobalDroppable } from '@frameio/shared/dnd';
import type { DisplayCategories } from '@frameio/vapor';
import {
  Box,
  ListItemContextMenu,
  ListItemLabelInlineEdit,
  MediaThumbnail,
  MediaThumbnailStack,
  Text,
  theme,
  useIsListBoxItemCurrentTabStop,
} from '@frameio/vapor';
import { useProjectNavigationActivation } from '../ProjectNavActivationManager';
import TEXT from './generated/TEXT/ShareNavItem';
import {
  ShareNavItemContent,
  ShareSkeletonThumbnail,
  ThumbnailWrapper,
} from './Shares.styles';

TEXT.DEFINE(
  {
    /** Aria-label for editing a share name */
    labelEdit: 'Edit Share name',
    /** Label for the share item count */
    itemCount: (count: number) =>
      plural(count, `${count} Items`, '1 Item', 'No Items'),
    /** Aria-label for the share actions menu button */
    showShareActionsMenu: 'Show share actions menu',
  },
  {
    plural,
  }
);

export type ShareDndNamespace = 'shares-nav';

type ShareNavItemProps = {
  dndNamespace: ShareDndNamespace;
  editing?: boolean;
  menuContent?: ReactNode;
  onRename?: (name: string) => void;
  shareId: string;
};

type NavThumbnail = {
  id: string;
  assetTypename: string;
  displayCategory: DisplayCategories;
  media: {
    thumbs: {
      thumb: string;
    };
  };
};

const ShareNavComponent = memo(
  ({
    editing: editingFromProps = false,
    menuContent,
    onRename = noop,
    shareId,
  }: ShareNavItemProps) => {
    const { state: navigationState } = useProjectNavigationActivation();
    const [__, activeId] = navigationState.split(':');

    const { data: share } = useShareNavItemFragment(shareId);
    // @note: re-enable when SHU-8390 is fixed
    // const assetCount =
    //   (share?.aggregationInfo?.fileCount ?? 0) +
    //   (share?.aggregationInfo?.folderCount ?? 0);
    const shareName = share?.name ?? '';
    const canRename = !share?.useOriginHeader;
    const thumbnailAssets: NavThumbnail[] = useMemo(
      () =>
        (share?.thumbnailAssets ?? []).map((thumbnail) => ({
          id: thumbnail?.id ?? '',
          assetTypename: thumbnail?.assetType ?? '',
          displayCategory: (thumbnail?.assetType === 'FOLDER'
            ? 'folder'
            : thumbnail?.filetype ?? 'unsupported') as DisplayCategories,
          media: {
            thumbs: {
              thumb: thumbnail?.thumbnailImageUrl?.url ?? '',
            },
            metadata: {
              hasAlpha: thumbnail?.hasAlpha ?? false,
            },
          },
        })),
      [share?.thumbnailAssets]
    );

    const handleSubmit = (newValue: string) => {
      onRename(newValue);
    };

    const thumbnailComponent = (
      <ThumbnailWrapper>
        {thumbnailAssets.length > 0 ? (
          <MediaThumbnailStack
            assets={thumbnailAssets}
            mode="fill"
            renderThumb={(asset, options) => (
              <MediaThumbnail
                {...asset}
                {...options}
                data-dd-action-name="share-nav.open"
                data-testid="share-nav-thumbnail"
                style={{ border: `.5px solid ${theme.colors.border.neutral}` }}
              />
            )}
            size="xsmall"
          />
        ) : (
          <ShareSkeletonThumbnail />
        )}
      </ThumbnailWrapper>
    );

    const isCurrentTabStop = useIsListBoxItemCurrentTabStop();
    const [isEditingLocal, setIsEditingLocal] = useState(false);
    const editing = isEditingLocal || editingFromProps;
    const isInlineEditEnabled = editing || isCurrentTabStop || false;

    const labelContent = (
      <Box display="flex" flexDirection="column" gap={2} paddingY={4}>
        {canRename ? (
          <ListItemLabelInlineEdit
            key={shareName}
            aria-label={TEXT.labelEdit}
            defaultValue={shareName}
            editing={editing}
            enabled={isInlineEditEnabled}
            enableTooltip={true}
            onEditingChange={(edit) => {
              setIsEditingLocal(edit);
              onRename(shareName);
            }}
            onSubmit={handleSubmit}
          />
        ) : (
          <Text
            color="body"
            enableTooltip={true}
            truncate={true}
            variant="body"
          >
            {shareName}
          </Text>
        )}

        {/* @note: re-enable when SHU-8390 is fixed */}
        {/* <Text color="secondary" truncate={true} variant="caption">
          {TEXT.itemCount(assetCount)}
        </Text> */}
      </Box>
    );

    return (
      <ShareNavItemContent
        $isActive={activeId === shareId}
        $isFocused={isCurrentTabStop}
        adornmentAfter={
          <ListItemContextMenu
            buttonAccessibilityLabel={TEXT.showShareActionsMenu}
            content={menuContent}
            variant="default"
          />
        }
        adornmentBefore={thumbnailComponent}
        label={labelContent}
      />
    );
  }
);

type ShareNavItemContainerProps = {
  children: React.ReactNode;
  dndNamespace: ShareDndNamespace;
  shareId: string;
};

function ShareNavItemContainer({
  children,
  shareId,
  dndNamespace,
}: ShareNavItemContainerProps) {
  const projectId = useActiveProjectId();
  const { data: share } = useShareNavItemFragment(shareId);
  const shareName = share?.name ?? '';
  const isCollectionShare = share?.__typename === 'CollectionShare';
  const { bind } = usePrefetchForShareBuilder({ shareId, projectId });
  const containerRef = useRef<HTMLDivElement>(null);
  const openContextMenu = useOpenContextMenu();
  const { setNodeRef, isOver, active } = useGlobalDroppable({
    id: shareId,
    data: { namespace: dndNamespace, shareName },
    disabled: isCollectionShare,
  });

  let dropEnabled = Boolean(active);

  if (isOver && active) {
    dropEnabled = active.id !== shareId;
  }

  const isDisabled = Boolean(active?.id && isCollectionShare);

  const handleContextMenu = (event?: MouseEvent) => {
    if (containerRef.current) {
      openContextMenu(shareId, containerRef.current, event);
    }
    return true;
  };
  return (
    <DropContainer
      ref={(node) => {
        setNodeRef(node);
        (containerRef as React.RefObject<HTMLDivElement | null>).current = node;
      }}
      $isBeingDraggedOver={isOver && dropEnabled}
      $isDisabled={isDisabled}
      data-testid={`share-nav-${shareName}`}
      onContextMenu={(e) => {
        e.preventDefault();
        handleContextMenu(e);
      }}
      {...bind}
    >
      {children}
    </DropContainer>
  );
}

export const ShareNavItem = React.memo(
  (
    props: Omit<ShareNavItemProps, 'shareName' | 'canRename' | 'assetCount'>
  ) => {
    const { shareId } = props;
    return (
      <ShareNavItemContainer
        dndNamespace={props.dndNamespace}
        shareId={shareId}
      >
        <ShareNavComponent {...props} />
      </ShareNavItemContainer>
    );
  }
);

const DropContainer = styled.div<{
  $isBeingDraggedOver: boolean;
  $isDisabled: boolean;
}>`
  align-items: center;
  border: transparent 1px solid;
  display: flex;
  opacity: ${(props) => (props.$isDisabled ? 0.5 : 1)};
  width: 100%;

  ${(props) =>
    props.$isBeingDraggedOver &&
    `
    background-color: ${theme.colors.brand.primaryAlphaLow};
    border-color: ${theme.colors.brand.primary};
  `}
`;
